import { join } from "node:path";
import { storefrontTemplateDir } from "./paths.js";
import { Brand, deriveAccentLight, deriveAccentPaletteSubs, renderEnv } from "./brand.js";
import { copyTree, exists, pruneTree, readFile, replaceInTree, writeFile } from "./fsx.js";

/**
 * Scaffold a white-labeled storefront from the bundled template.
 *
 * Strategy: the template's own `src/brand.config.ts` is the canonical schema the
 * components consume (it carries extra fields like supportWhatsapp/accentLight).
 * We PATCH its values in place rather than regenerate it, so we never drop a field
 * a component depends on. Tree-wide replacements are limited to hex colors, which
 * are unambiguous — never the `carrier.llc` apex, which is also the real api./mcp.
 * control-plane host.
 *
 * There is deliberately no tree-wide brand-name swap. The template carries no
 * prior brand's name to replace: `storefront-brand-free.test.js` holds it that
 * way, and every name a page prints comes from brand.config.ts.
 */
/** Keep non-empty Clerk keys from a prior `.env.local` when re-scaffolding. */
function preserveClerkFromEnv(existing: string, fresh: string): string {
  let out = fresh;
  for (const key of ["NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY", "CLERK_SECRET_KEY"] as const) {
    const line = existing.split("\n").find((l) => l.startsWith(`${key}=`));
    if (!line) continue;
    const value = line.slice(key.length + 1);
    if (!value.trim()) continue;
    out = out.replace(new RegExp(`^${key}=.*$`, "m"), `${key}=${value}`);
  }
  return out;
}

export async function scaffoldStorefront(target: string, brand: Brand): Promise<void> {
  const template = storefrontTemplateDir();
  if (!(await exists(template))) {
    throw new Error(
      `Storefront template not found at ${template}. The @carrierllc/mcp package may be corrupt — reinstall it.`,
    );
  }

  const envPath = join(target, ".env.local");
  const existingEnv = (await exists(envPath)) ? await readFile(envPath, "utf8") : undefined;

  await copyTree(template, target);
  await pruneTree(template, target);

  // Tree-wide, unambiguous swaps only: hex colors, which appear in globals.css
  // and brand.config.ts and nowhere a false positive could hide.
  const subs: Array<[string, string]> = [
    ...deriveAccentPaletteSubs(brand.colors.accent, brand.colors.accentDark),
  ];
  if (brand.colors.bg.toUpperCase() !== "#080C16") subs.push(["#080C16", brand.colors.bg]);
  if (brand.colors.text.toUpperCase() !== "#F5F1EA") subs.push(["#F5F1EA", brand.colors.text]);
  await replaceInTree(target, subs);

  // Patch the canonical brand.config.ts field values in place.
  await patchBrandConfig(join(target, "src", "brand.config.ts"), brand);

  // Runtime env (brand name + control-plane URL).
  const envBody = existingEnv ? preserveClerkFromEnv(existingEnv, renderEnv(brand)) : renderEnv(brand);
  await writeFile(envPath, envBody);

  // npm strips nested .npmrc on publish, so (re)write it for standalone installs —
  // lets `pnpm install` work even when the storefront is created inside a workspace.
  await writeFile(join(target, ".npmrc"), "ignore-workspace-root-check=true\n");
}

/** Field-scoped value patches against the template's brand.config.ts — preserves its schema. */
async function patchBrandConfig(path: string, brand: Brand): Promise<void> {
  if (!(await exists(path))) return;
  let src = await readFile(path, "utf8");

  const setField = (field: string, value: string): void => {
    // matches `  field: "..."` or `  field: process.env.X ?? "..."` — replaces the LAST quoted literal on that key's line
    const re = new RegExp(`(\\b${field}:\\s*(?:[^"\\n]*\\?\\?\\s*)?)"[^"]*"`);
    src = src.replace(re, `$1${JSON.stringify(value)}`);
  };

  setField("name", brand.name);
  setField("legalName", brand.legalName);
  setField("tagline", brand.tagline);
  setField("domain", brand.domain);
  setField("supportUrl", brand.supportUrl);
  setField("supportEmail", brand.supportEmail);
  setField("supportWhatsapp", brand.supportWhatsapp);
  setField("description", brand.description);
  setField("emailFromName", brand.emailFromName);
  setField("emailFromMailbox", brand.emailFromMailbox);
  setField("appUrl", brand.appUrl);
  setField("accent", brand.colors.accent);
  setField("accentDark", brand.colors.accentDark);
  setField("accentLight", deriveAccentLight(brand.colors.accent));
  setField("bg", brand.colors.bg);

  const socialEntries = Object.entries(brand.social).filter(([, v]) => v);
  const socialBody = socialEntries.map(([k, v]) => `    ${k}: ${JSON.stringify(v)},`).join("\n");
  src = src.replace(
    /social:\s*\{[^}]*\}/s,
    socialEntries.length ? `social: {\n${socialBody}\n  }` : "social: {}",
  );

  await writeFile(path, src);
}
